import type { PublicFunnelEventName, PublicFunnelEventPayload } from '@shared/public-analytics';

const STORAGE_KEY = 'kalovate.publicFunnelAnonymousId';

function readCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf-token') {
      return decodeURIComponent(value);
    }
  }

  return null;
}

function generateAnonymousId() {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `anon-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function getPublicAnalyticsAnonymousId(): string {
  try {
    const existingId = window.localStorage.getItem(STORAGE_KEY);
    if (existingId) {
      return existingId;
    }

    const nextId = generateAnonymousId();
    window.localStorage.setItem(STORAGE_KEY, nextId);
    return nextId;
  } catch {
    return generateAnonymousId();
  }
}

type PublicFunnelEventInput = Omit<
  PublicFunnelEventPayload,
  'anonymousId' | 'occurredAt' | 'pathname' | 'search' | 'viewportWidth' | 'viewportHeight'
>;

export function buildPublicFunnelEventPayload(
  input: PublicFunnelEventInput,
  locationSnapshot: { pathname: string; search: string },
  anonymousId: string,
  viewport: { width?: number; height?: number } = {}
): PublicFunnelEventPayload {
  return {
    ...input,
    anonymousId,
    occurredAt: new Date().toISOString(),
    pathname: locationSnapshot.pathname,
    search: locationSnapshot.search,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
  };
}

export async function trackPublicFunnelEvent(
  input: PublicFunnelEventInput & { keepalive?: boolean }
): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const { keepalive = false, ...event } = input;
  const payload = buildPublicFunnelEventPayload(
    event,
    {
      pathname: window.location.pathname,
      search: window.location.search,
    },
    getPublicAnalyticsAnonymousId(),
    {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  );

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const csrfToken = readCsrfToken();
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  try {
    await fetch('/api/analytics/public-funnel', {
      method: 'POST',
      credentials: 'include',
      keepalive,
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Analytics must never block the primary interaction.
  }
}

export type { PublicFunnelEventName };
